import React, { useEffect, useState, useRef, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Link, useSearchParams } from "react-router-dom";

import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import SearchIcon from "@mui/icons-material/Search";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";

import AddIcon from "@mui/icons-material/Add";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import CommonDataService from "../../services/common";
import { parseDate, clean, parseNumber } from "../../helpers/common";
import {
	setCurrentPageSize,
	setLoading,
	setModule,
} from "../../store/actions/commonAction";

import MessageDialog from "../MessageDialog";
import DeleteDialog from "../DeleteDialog";
import Pager from "../Pager";

export default function CareerList() {
	const [searchParams, setSearchParams] = useSearchParams();

	let currentPage = clean(searchParams.get("page"));
	if (currentPage === "") {
		currentPage = 1;
	}

	let s = clean(searchParams.get("s"));
	if (s === "") {
		s = "n.publish_time";
	}

	let d = clean(searchParams.get("d"));
	if (d === "") {
		d = "desc";
	}

	const params = useRef();
	const idCategory = useRef(clean(searchParams.get("category")));
	const idLevel = useRef(clean(searchParams.get("level")));
	const idLocation = useRef(clean(searchParams.get("location")));
	const name = useRef(clean(searchParams.get("name")));
	const active = useRef(clean(searchParams.get("active")));
	const orderBy = useRef(s);
	const order = useRef(d);

	const [rows, setRows] = useState([]);
	const [count, setCount] = useState(0);
	const [message, setMessage] = useState("");
	const [error, setError] = useState(false);
	const [del, setDel] = useState(false);
	const [delID, setDelID] = useState(0);
	const [page, setPage] = useState(parseNumber(currentPage));

	const [categories, setCategories] = useState([]);
	const [levels, setLevels] = useState([]);
	const [locations, setLocations] = useState([]);

	const currentPageSize = useSelector(
		(state) => state.commonReducer.currentPageSize
	);

	const orders = useSelector((state) => state.commonReducer.orders);
	const pageSizes = useSelector((state) => state.commonReducer.pageSizes);

	const dispatch = useDispatch();

	const sorts = [
		{
			label: "Category",
			value: "cat.name",
		},
		{
			label: "Level",
			value: "lev.name",
		},
		{
			label: "Location",
			value: "loc.name",
		},
		{
			label: "Publish Time",
			value: "c.publish_time",
		},
		{
			label: "Status",
			value: "c.active",
		},
	];

	const LoadData = useCallback(() => {
		let pars = {
			category: idCategory.current,
			level: idLevel.current,
			location: idLocation.current,
			name: name.current,
			active: active.current,
			s: orderBy.current,
			d: order.current,
			page: page,
			perpage: currentPageSize,
		};

		dispatch(setLoading(true));
		CommonDataService.getCareer(pars)
			.then((res) => {
				setRows(res.data);
				setCount(res.data.total_pages);
				dispatch(setLoading(false));
			})
			.catch(function (error) {
				setMessage(error.response.data.message);
				setError(true);
				dispatch(setLoading(false));
			});
	}, [dispatch, page, currentPageSize]);

	useEffect(() => {
		LoadData();
	}, [LoadData, dispatch, page, currentPageSize]);

	const LoadSelect = useCallback(() => {
		CommonDataService.getCareerCategory().then((res) => {
			setCategories(res.data);
		});
		CommonDataService.getCareerLevel().then((res) => {
			setLevels(res.data);
		});
		CommonDataService.getCareerLocation().then((res) => {
			setLocations(res.data);
		});
	}, []);

	useEffect(() => {
		LoadSelect();
	}, [LoadSelect]);

	useEffect(() => {
		dispatch(setModule("Career"));
	}, [dispatch]);

	const handleSubmit = (event) => {
		event.preventDefault();
		const data = new FormData(event.currentTarget);
		idCategory.current = data.get("idCategory");
		idLevel.current = data.get("idLevel");
		idLocation.current = data.get("idLocation");
		name.current = data.get("name");
		active.current = data.get("active");
		orderBy.current = data.get("orderBy");
		order.current = data.get("order");
		setPage(1);
		setSearchParams(searchParams);
		LoadData();
	};

	const confirmDelete = (id, title) => {
		setDel(true);
		setDelID(id);
		setMessage(title);
	};

	const Delete = () => {
		setDel(false);
		params.current = {};

		dispatch(setLoading(true));
		CommonDataService.deleteCareer(delID)
			.then((res) => {
				LoadData();
			})
			.catch(function (error) {
				setMessage(error.response.data.message);
				setError(true);
				dispatch(setLoading(false));
			});
	};

	return (
		<Grid container spacing={3}>
			<Grid item xs={12}>
				<Paper sx={{ p: 2, display: "flex", flexDirection: "column" }}>
					<Box component="form" onSubmit={(e) => handleSubmit(e)}>
						<Typography variant="h5">Search Career Form</Typography>

						<Grid container spacing={3}>
							<Grid item xs={12} sm={4}>
								<FormControl
									fullWidth={true}
									variant="standard"
									sx={{ minWidth: 100 }}
								>
									<InputLabel id="id-category-label">Category</InputLabel>
									<Select
										labelId="id-category-label"
										id="idCategory"
										name="idCategory"
										defaultValue={idCategory.current}
									>
										<MenuItem value="">
											<em>None</em>
										</MenuItem>
										{categories.map((option, index) => (
											<MenuItem key={index} value={option.id}>
												{option.name}
											</MenuItem>
										))}
									</Select>
								</FormControl>
							</Grid>
							<Grid item xs={12} sm={4}>
								<FormControl
									fullWidth={true}
									variant="standard"
									sx={{ minWidth: 100 }}
								>
									<InputLabel id="id-level-label">Level</InputLabel>
									<Select
										labelId="id-level-label"
										id="idLevel"
										name="idLevel"
										defaultValue={idLevel.current}
									>
										<MenuItem value="">
											<em>None</em>
										</MenuItem>
										{levels.map((option, index) => (
											<MenuItem key={index} value={option.id}>
												{option.name}
											</MenuItem>
										))}
									</Select>
								</FormControl>
							</Grid>
							<Grid item xs={12} sm={4}>
								<FormControl
									fullWidth={true}
									variant="standard"
									sx={{ minWidth: 100 }}
								>
									<InputLabel id="id-location-label">Location</InputLabel>
									<Select
										labelId="id-location-label"
										id="idLocation"
										name="idLocation"
										defaultValue={idLocation.current}
									>
										<MenuItem value="">
											<em>None</em>
										</MenuItem>
										{locations.map((option, index) => (
											<MenuItem key={index} value={option.id}>
												{option.name}
											</MenuItem>
										))}
									</Select>
								</FormControl>
							</Grid>
							<Grid item xs={12} sm={4}>
								<TextField
									label="Search by title"
									id="name"
									name="name"
									fullWidth
									defaultValue={name.current}
									variant="standard"
								/>
							</Grid>

							<Grid item xs={12} sm={2}>
								<FormControl
									fullWidth={true}
									variant="standard"
									sx={{ minWidth: 100 }}
								>
									<InputLabel id="id-active-label">Status</InputLabel>
									<Select
										id="active"
										name="active"
										labelId="id-active-label"
										defaultValue={active.current}
									>
										<MenuItem value="">
											<em>All</em>
										</MenuItem>
										<MenuItem value="1">Active</MenuItem>
										<MenuItem value="0">Inactive</MenuItem>
									</Select>
								</FormControl>
							</Grid>

							<Grid item xs={12} sm={3}>
								<FormControl
									fullWidth={true}
									variant="standard"
									sx={{ minWidth: 100 }}
								>
									<InputLabel id="orderbylabel">Order by</InputLabel>
									<Select
										id="orderBy"
										name="orderBy"
										labelId="orderbylabel"
										label="Order By"
										defaultValue={orderBy.current}
									>
										{sorts.map((option, index) => (
											<MenuItem key={index} value={option.value}>
												{option.label}
											</MenuItem>
										))}
									</Select>
								</FormControl>
							</Grid>

							<Grid item xs={12} sm={3}>
								<FormControl
									fullWidth={true}
									variant="standard"
									sx={{ minWidth: 100 }}
								>
									<InputLabel id="orderlabel">Sort</InputLabel>
									<Select
										id="order"
										name="order"
										labelId="orderlabel"
										label="Sort"
										defaultValue={order.current}
									>
										{orders.map((option, index) => (
											<MenuItem key={index} value={option.value}>
												{option.label}
											</MenuItem>
										))}
									</Select>
								</FormControl>
							</Grid>

							<Grid item xs={6} sm={6}>
								<Button
									type="submit"
									variant="contained"
									startIcon={<SearchIcon />}
								>
									Search
								</Button>
							</Grid>
							<Grid item xs={6} sm={6} container justifyContent="flex-end">
								<Button
									variant="contained"
									component={Link}
									to="/addcareer"
									startIcon={<AddIcon />}
								>
									Create
								</Button>
							</Grid>
						</Grid>
					</Box>
				</Paper>
			</Grid>

			<Grid item xs={12}>
				<Paper sx={{ p: 2, display: "flex", flexDirection: "column" }}>
					<Typography variant="h4" gutterBottom component="div">
						Career List
					</Typography>

					<TableContainer component={Paper}>
						<Table sx={{}} size="small">
							<TableHead>
								<TableRow>
									<TableCell width={5}>No.</TableCell>
									<TableCell>Title</TableCell>
									<TableCell>Category</TableCell>
									<TableCell>Level</TableCell>
									<TableCell>Location</TableCell>
									<TableCell align="center">Publish Time</TableCell>
									<TableCell align="center">Status</TableCell>
									<TableCell align="center">Actions</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{rows.Data &&
									rows.Data.map((row, index) => (
										<TableRow
											key={index}
											sx={{
												"&:last-child td, &:last-child th": { border: 0 },
											}}
										>
											<TableCell align="right">
												{rows.first_no + index}
											</TableCell>
											<TableCell>{row.title}</TableCell>
											<TableCell>{row.category}</TableCell>
											<TableCell>{row.level}</TableCell>
											<TableCell>{row.location}</TableCell>
											<TableCell align="center">
												{parseDate(row.publish_time)}
											</TableCell>
											<TableCell align="center">
												{row.active ? "yes" : "no"}
											</TableCell>
											<TableCell align="center">
												<Link
													to={
														"/editcareer/" +
														row.id +
														"?category=" +
														idCategory.current +
														"&level=" +
														idLevel.current +
														"&location=" +
														idLocation.current +
														"&name=" +
														name.current +
														"&active=" +
														active.current +
														"&s=" +
														orderBy.current +
														"&d=" +
														order.current +
														"&page=" +
														page
													}
													className="badge badge-warning"
												>
													<EditIcon fontSize="small" />
												</Link>
												<DeleteIcon
													fontSize="small"
													onClick={() => confirmDelete(row.id, row.title)}
												/>
											</TableCell>
										</TableRow>
									))}
							</TableBody>

							<Pager
								colSpan={8}
								count={count}
								dispatch={dispatch}
								page={page}
								pageSizes={pageSizes}
								currentPageSize={currentPageSize}
								setPage={setPage}
								setCurrentPageSize={setCurrentPageSize}
							/>
						</Table>
					</TableContainer>

					<MessageDialog
						show={error}
						message={message}
						dialogClose={setError}
					/>

					<DeleteDialog
						show={del}
						message={message}
						dialogClose={setDel}
						Delete={Delete}
					/>
				</Paper>
			</Grid>
		</Grid>
	);
}
